import { useRef, useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../api/axios';

const LOGIN_URL = '/auth/login';

const AdminLogin = () => {
    const { setAuth, persist, setPersist } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/admin';

    const userRef = useRef();
    const errRef = useRef();

    const [user, setUser] = useState('');
    const [pwd, setPwd] = useState('');
    const [errMsg, setErrMsg] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => { userRef.current?.focus(); }, []);
    useEffect(() => { setErrMsg(''); }, [user, pwd]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(LOGIN_URL,
                { userid: user, password: pwd },
                { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
            );
            const accessToken = response?.data?.accessToken;
            setAuth({ userid: user, accessToken });
            setUser('');
            setPwd('');
            navigate(from, { replace: true });
        } catch (err) {
            if (!err?.response) setErrMsg('No Server Response');
            else if (err.response?.status === 400) setErrMsg('Missing Username or Password');
            else if (err.response?.status === 401) setErrMsg('Invalid credentials');
            else setErrMsg('Login Failed');
            errRef.current?.focus();
        } finally {
            setLoading(false);
        }
    };

    const togglePersist = () => {
        setPersist(prev => {
            localStorage.setItem('persist', JSON.stringify(!prev));
            return !prev;
        });
    };

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
            <div className="w-full max-w-sm">

                {/* Logo */}
                <div className="text-center mb-10">
                    <Link to="/">
                        <img src="/images/Logo.png" alt="Kia Kaha" className="h-14 w-auto mx-auto mb-4" />
                    </Link>
                    <h1 className="font-serif text-3xl font-light text-stone-800">Admin</h1>
                    <div className="w-8 h-px bg-jade-400 mx-auto mt-3" />
                </div>

                {/* Error */}
                {errMsg && (
                    <p
                        ref={errRef}
                        className="mb-6 text-center font-sans text-sm text-red-500"
                        aria-live="assertive"
                    >
                        {errMsg}
                    </p>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                            Username
                        </label>
                        <input
                            type="text"
                            ref={userRef}
                            autoComplete="username"
                            value={user}
                            onChange={e => setUser(e.target.value)}
                            required
                            className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                         focus:outline-none focus:border-jade-500 transition-colors"
                        />
                    </div>

                    <div>
                        <label className="block font-sans text-xs tracking-widest uppercase text-stone-400 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={pwd}
                            onChange={e => setPwd(e.target.value)}
                            required
                            className="w-full border-b border-stone-300 bg-transparent py-3 font-sans text-stone-700
                         focus:outline-none focus:border-jade-500 transition-colors"
                        />
                    </div>

                    {/* Trust device */}
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={persist}
                            onChange={togglePersist}
                            className="w-4 h-4 accent-jade-500"
                        />
                        <span className="font-sans text-xs tracking-widest uppercase text-stone-400">
                            Trust this device
                        </span>
                    </label>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full btn-jade justify-center disabled:opacity-50"
                    >
                        {loading ? 'Signing in…' : 'Sign In'}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <Link to="/" className="font-sans text-xs tracking-widest uppercase text-stone-400 hover:text-jade-500 transition-colors">
                        ← Back to Site
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
